import { create } from "zustand";
import { notificationsApi } from "@/lib/api";
import { supabase, isRealtimeEnabled } from "@/lib/supabase";
import type { Notification, NotificationPreferences } from "@/types";
import type { RealtimeChannel } from "@supabase/supabase-js";

interface NotificationStore {
  // State
  notifications: Notification[];
  preferences: NotificationPreferences | null;
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  subscription: RealtimeChannel | null;

  // Actions
  fetchNotifications: (options?: {
    is_read?: boolean;
    limit?: number;
  }) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  fetchPreferences: () => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
  updatePreferences: (
    prefs: Partial<NotificationPreferences>,
  ) => Promise<boolean>;
  refresh: () => Promise<void>;
  subscribeToNotifications: (userId: string) => void;
  unsubscribeFromNotifications: () => void;

  // Realtime callbacks (pre centrálny RealtimeContext manager)
  handleRealtimeInsert: (notification: Notification) => void;
  handleRealtimeUpdate: (notification: Notification) => void;
  handleRealtimeDelete: (notificationId: number) => void;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  // Initial state
  notifications: [],
  preferences: null,
  unreadCount: 0,
  isLoading: false,
  error: null,
  subscription: null,

  /**
   * Načítať notifikácie
   */
  fetchNotifications: async (options) => {
    set({ isLoading: true, error: null });
    try {
      const response = await notificationsApi.list(options);
      if (response.success) {
        // Vždy nastaviť notifications, aj keď je prázdny array
        set({ notifications: response.data || [] });
      } else {
        set({
          error: response.error || "Failed to fetch notifications",
          notifications: [], // Resetovať na prázdny array pri chybe
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Unknown error",
        notifications: [], // Resetovať na prázdny array pri chybe
      });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Načítať počet neprečítaných notifikácií
   */
  fetchUnreadCount: async () => {
    try {
      const response = await notificationsApi.getUnreadCount();
      if (response.success && response.count !== undefined) {
        set({ unreadCount: response.count });
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  },

  /**
   * Načítať preferencie notifikácií
   */
  fetchPreferences: async () => {
    try {
      const response = await notificationsApi.getPreferences();
      if (response.success && response.data) {
        set({ preferences: response.data });
      }
    } catch (error) {
      console.error("Failed to fetch preferences:", error);
    }
  },

  /**
   * Označiť notifikáciu ako prečítanú
   */
  markAsRead: async (notificationId: number) => {
    const response = await notificationsApi.markAsRead(notificationId);
    if (response.success) {
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    }
  },

  /**
   * Označiť všetky notifikácie ako prečítané
   */
  markAllAsRead: async () => {
    const response = await notificationsApi.markAllAsRead();
    if (response.success) {
      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          is_read: true,
        })),
        unreadCount: 0,
      }));
    }
  },

  /**
   * Vymazať notifikáciu
   */
  deleteNotification: async (notificationId: number) => {
    const response = await notificationsApi.delete(notificationId);
    if (response.success) {
      set((state) => {
        const notification = state.notifications.find(
          (n) => n.id === notificationId,
        );
        return {
          notifications: state.notifications.filter(
            (n) => n.id !== notificationId,
          ),
          unreadCount:
            notification && !notification.is_read
              ? Math.max(0, state.unreadCount - 1)
              : state.unreadCount,
        };
      });
    }
  },

  /**
   * Aktualizovať preferencie notifikácií
   */
  updatePreferences: async (prefs) => {
    set({ isLoading: true, error: null });
    try {
      const response = await notificationsApi.updatePreferences(prefs);
      if (response.success) {
        if (response.data) {
          set({ preferences: response.data });
        }
        return true;
      }

      set({ error: response.error || "Failed to update preferences" });
      return false;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Unknown error",
      });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Obnoviť notifikácie a počet neprečítaných
   */
  refresh: async () => {
    await Promise.all([get().fetchNotifications(), get().fetchUnreadCount()]);
  },

  /**
   * Prihlásiť sa na real-time zmeny notifikácií (lazy – volané z Dashboard/NotificationsPage)
   * Používa cielené aktualizácie cache namiesto plného re-fetch
   */
  subscribeToNotifications: (userId: string) => {
    if (!isRealtimeEnabled) return;

    const currentSubscription = get().subscription;
    if (currentSubscription) {
      void supabase.removeChannel(currentSubscription);
    }

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const store = get();
          if (payload.eventType === "INSERT" && payload.new) {
            store.handleRealtimeInsert(payload.new as unknown as Notification);
            void store.fetchUnreadCount();
          } else if (payload.eventType === "UPDATE" && payload.new) {
            store.handleRealtimeUpdate(payload.new as unknown as Notification);
            const newN = payload.new as unknown as Notification;
            const oldN = payload.old as unknown as Notification | undefined;
            if (newN?.is_read !== oldN?.is_read) void store.fetchUnreadCount();
          } else if (payload.eventType === "DELETE" && payload.old) {
            const oldN = payload.old as unknown as Notification;
            if (oldN?.id) {
              store.handleRealtimeDelete(oldN.id);
              void store.fetchUnreadCount();
            }
          }
        },
      )
      .subscribe();

    set({ subscription: channel });
  },

  /**
   * Odpojiť sa od real-time zmien notifikácií
   */
  unsubscribeFromNotifications: () => {
    const subscription = get().subscription;
    if (subscription) {
      void supabase.removeChannel(subscription);
      set({ subscription: null });
    }
  },

  /**
   * Realtime: Pridať novú notifikáciu (callback pre RealtimeContext)
   */
  handleRealtimeInsert: (notification: Notification) => {
    set((state) => {
      // Skontrolovať, či už existuje (duplicity)
      const exists = state.notifications.some((n) => n.id === notification.id);
      if (exists) {
        return state;
      }

      // Pridať na začiatok zoznamu
      return {
        notifications: [notification, ...state.notifications],
        unreadCount: notification.is_read
          ? state.unreadCount
          : state.unreadCount + 1,
      };
    });
  },

  /**
   * Realtime: Aktualizovať existujúcu notifikáciu (callback pre RealtimeContext)
   */
  handleRealtimeUpdate: (notification: Notification) => {
    set((state) => {
      const existingIndex = state.notifications.findIndex(
        (n) => n.id === notification.id,
      );
      if (existingIndex === -1) {
        // Ak neexistuje, pridať ju
        return {
          notifications: [notification, ...state.notifications],
          unreadCount: notification.is_read
            ? state.unreadCount
            : state.unreadCount + 1,
        };
      }

      const existingNotification = state.notifications[existingIndex];
      const wasRead = existingNotification.is_read;
      const isRead = notification.is_read;

      // Aktualizovať notifikáciu
      const updatedNotifications = [...state.notifications];
      updatedNotifications[existingIndex] = notification;

      // Aktualizovať unreadCount
      let newUnreadCount = state.unreadCount;
      if (!wasRead && isRead) {
        newUnreadCount = Math.max(0, newUnreadCount - 1);
      } else if (wasRead && !isRead) {
        newUnreadCount = newUnreadCount + 1;
      }

      return {
        notifications: updatedNotifications,
        unreadCount: newUnreadCount,
      };
    });
  },

  /**
   * Realtime: Odstrániť notifikáciu (callback pre RealtimeContext)
   */
  handleRealtimeDelete: (notificationId: number) => {
    set((state) => {
      const notification = state.notifications.find(
        (n) => n.id === notificationId,
      );
      return {
        notifications: state.notifications.filter(
          (n) => n.id !== notificationId,
        ),
        unreadCount:
          notification && !notification.is_read
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount,
      };
    });
  },
}));
